import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { api } from '../services/api';

// Prefixo que marca uma posição mapeada para TEXTO FIXO em vez de variável do
// sistema. Precisa bater com LITERAL_PREFIX de backend/services/templateVariableMapping.
const TEXT_MAPPING_PREFIX = 'text:';

// Tipos que a Meta aceita em cada formato de cabeçalho (o backend revalida).
const HEADER_ACCEPT: Record<string, string> = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

interface WhatsAppAccount {
  id: string;
  platformAccountId: string;
  accountName: string;
  accountUsername: string;
  accountAvatar: string | null;
  groupName: string;
}

interface WhatsAppTemplate {
  id?: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components?: any[];
  variableOrder?: string[];
  rejectedReason?: string;
}

interface Broadcast {
  id: string;
  name: string;
  status: string;
  template?: any;
  recipientCount?: number;
  sent?: number;
  failed?: number;
  scheduledAt?: string;
  createdAt?: string;
}

type TabType = 'templates' | 'criar' | 'broadcasts';

const TEMPLATE_CATEGORIES = [
  { value: 'AUTHENTICATION', label: 'Autenticação', description: 'Códigos de verificação e acesso' },
  { value: 'MARKETING', label: 'Marketing', description: 'Promoções, ofertas, novidades' },
  { value: 'UTILITY', label: 'Utilidade', description: 'Confirmações, atualizações, alertas' },
];

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (BR)' },
  { value: 'en', label: 'English' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'es', label: 'Español' },
  { value: 'es_AR', label: 'Español (AR)' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
];

const STATUS_COLORS: Record<string, string> = {
  APPROVED: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  REJECTED: 'bg-red-100 text-red-700',
  PAUSED: 'bg-gray-100 text-gray-600',
  DISABLED: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS: Record<string, string> = {
  APPROVED: 'Aprovado',
  PENDING: 'Em revisão',
  REJECTED: 'Rejeitado',
  PAUSED: 'Pausado',
  DISABLED: 'Desativado',
};

export function WhatsAppTemplatesPage() {
  const [accounts, setAccounts] = useState<WhatsAppAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('templates');
  const [loading, setLoading] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);

  // Create template form
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('MARKETING');
  const [newLanguage, setNewLanguage] = useState('pt_BR');
  const [newHeaderFormat, setNewHeaderFormat] = useState<'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'>('NONE');
  const [newHeaderText, setNewHeaderText] = useState('');
  const [newHeaderMediaUrl, setNewHeaderMediaUrl] = useState('');
  const [uploadingHeaderMedia, setUploadingHeaderMedia] = useState(false);
  const [newBodyText, setNewBodyText] = useState('');
  const [newFooterText, setNewFooterText] = useState('');
  // urlExample: a URL completa de exemplo que a Meta usa para revisar um link
  // dinâmico. Só é usada quando a URL do botão contém uma variável.
  const [newButtons, setNewButtons] = useState<Array<{ type: string; text: string; url?: string; phoneNumber?: string; urlExample?: string }>>([]);
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  // Broadcast form
  const [showBroadcastForm, setShowBroadcastForm] = useState(false);
  const [broadcastName, setBroadcastName] = useState('');
  const [broadcastTemplate, setBroadcastTemplate] = useState('');
  const [broadcastRecipients, setBroadcastRecipients] = useState('');
  const [broadcastSchedule, setBroadcastSchedule] = useState('');
  const [creatingBroadcast, setCreatingBroadcast] = useState(false);

  // Template detail modal
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [selectedTemplateVariableOrder, setSelectedTemplateVariableOrder] = useState<string[]>([]);
  const [savingTemplateMapping, setSavingTemplateMapping] = useState(false);
  const [templateDetailMode, setTemplateDetailMode] = useState<'preview' | 'mapping'>('preview');

  // Search & filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const sanitizeTemplateName = (value: string) => {
    const normalized = value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const startsWithLetter = normalized.match(/^[a-z]/) ? normalized : `t_${normalized.replace(/^[^a-z]+/, '')}`;
    return startsWithLetter.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  };

  const handleSaveTemplateMapping = async () => {
    if (!selectedTemplate) return;

    const positions = extractTemplatePositions(selectedTemplate.components);
    if (positions.length === 0) {
      toast.error('Este template não possui variáveis posicionais para mapear');
      return;
    }

    // Cada posição precisa estar preenchida. Atenção ao texto fixo: só o prefixo
    // ("text:" sem conteúdo) é truthy, mas mandaria uma posição vazia ao WhatsApp.
    const unmapped = positions.some((_, index) => {
      const entry = selectedTemplateVariableOrder[index] || '';
      if (entry.startsWith(TEXT_MAPPING_PREFIX)) {
        return entry.slice(TEXT_MAPPING_PREFIX.length).trim() === '';
      }
      return !entry;
    });
    if (unmapped) {
      toast.error('Preencha todas as posições do template antes de salvar (variável ou texto fixo)');
      return;
    }

    try {
      setSavingTemplateMapping(true);
      await api.updateWhatsAppTemplateMapping({
        accountId: selectedAccountId,
        templateName: selectedTemplate.name,
        language: selectedTemplate.language,
        variableOrder: selectedTemplateVariableOrder,
      });

      setTemplates(prev => prev.map(template =>
        template.name === selectedTemplate.name && template.language === selectedTemplate.language
          ? { ...template, variableOrder: selectedTemplateVariableOrder }
          : template
      ));

      setSelectedTemplate(prev => prev ? { ...prev, variableOrder: selectedTemplateVariableOrder } : prev);
      toast.success('Mapeamento do template salvo com sucesso');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar mapeamento do template');
    } finally {
      setSavingTemplateMapping(false);
    }
  };

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.getWhatsAppAccounts();
      const accts = result.data || [];
      setAccounts(accts);
      if (accts.length > 0 && !selectedAccountId) {
        // platformAccountId é opcional no banco; cair para o id da conexão evita
        // mandar accountId vazio (backend responde "accountId é obrigatório").
        setSelectedAccountId(accts[0].platformAccountId || accts[0].id);
      }
    } catch (err: any) {
      console.error('Erro ao carregar contas:', err);
      toast.error('Erro ao carregar contas WhatsApp');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      setLoadingTemplates(true);
      const result = await api.getWhatsAppTemplates(selectedAccountId);
      setTemplates(result.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar templates:', err);
      toast.error(err.message || 'Erro ao carregar templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, [selectedAccountId]);

  const loadBroadcasts = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      setLoadingBroadcasts(true);
      const result = await api.getWhatsAppBroadcasts(selectedAccountId);
      setBroadcasts(result.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar broadcasts:', err);
    } finally {
      setLoadingBroadcasts(false);
    }
  }, [selectedAccountId]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  useEffect(() => {
    if (selectedAccountId) {
      loadTemplates();
      loadBroadcasts();
    }
  }, [selectedAccountId, loadTemplates, loadBroadcasts]);

  const handleUploadHeaderMedia = async (file: File) => {
    if (newHeaderFormat === 'NONE' || newHeaderFormat === 'TEXT') return;
    try {
      setUploadingHeaderMedia(true);
      const uploaded = await api.uploadWhatsAppTemplateHeaderMedia(file, newHeaderFormat);
      setNewHeaderMediaUrl(uploaded.url);
      toast.success('Arquivo enviado');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao enviar arquivo');
    } finally {
      setUploadingHeaderMedia(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newName.trim()) return toast.error('Nome do template é obrigatório');
    if (!newBodyText.trim()) return toast.error('Texto do corpo é obrigatório');

    const components: any[] = [];

    const VARIABLE_EXAMPLES_BY_KEY: Record<string, string> = {
      nome: 'João Silva',
      primeironome: 'João',
      telefone: '11999999999',
      email: 'joao@email.com',
      itens: 'Camiseta Branca P',
      numeropedido: '12345',
      numeronotafiscal: 'NF-67890',
      ecommerce: 'Loja Virtual',
      observacoes: 'Cliente VIP',
      categoria: 'Varejo',
      link: 'https://exemplo.com/oferta',
    };

    // Placeholder que não é variável conhecida ia CRU para a Meta (o template
    // saía com "{{algumacoisa}}" literal no texto). Barra antes de submeter.
    const unknownPlaceholders = [newHeaderText, newBodyText, ...newButtons.map(b => b.url || '')]
      .flatMap(text => [...String(text).matchAll(/\{\{\s*([^}]+?)\s*\}\}/gi)].map(m => String(m[1]).trim().toLowerCase()))
      .filter(key => !FRIENDLY_VARIABLES.some(variable => variable.key === key));
    if (unknownPlaceholders.length > 0) {
      return toast.error(`Variável desconhecida: ${[...new Set(unknownPlaceholders)].map(k => `{{${k}}}`).join(', ')}. Use os botões "Inserir variável".`);
    }

    // ORDEM DAS POSIÇÕES — precisa bater com o que a Zernio espera no envio:
    // variáveis do cabeçalho de TEXTO, depois do corpo, depois UMA por botão de
    // link dinâmico (na ordem dos botões). O rodapé fica de fora porque a Meta
    // não aceita variável nele (validado logo abaixo).
    const headerHasText = newHeaderFormat === 'TEXT' && newHeaderText.trim().length > 0;
    const textVariableOrder = [
      ...new Set([
        ...(headerHasText ? extractFriendlyVariableOrder(newHeaderText) : []),
        ...extractFriendlyVariableOrder(newBodyText),
      ])
    ];

    const variablePositions = new Map(
      textVariableOrder.map((key, index) => [key, String(index + 1)])
    );

    // Convert friendly variable names to positional IDs: {{nome}} → {{1}} using sequential order of appearance
    const normalizeTemplateVariables = (text: string) => text.replace(
      /\{\{\s*([^}]+?)\s*\}\}/gi,
      (match, rawName) => {
        const normalizedKey = String(rawName).trim().toLowerCase();
        const position = variablePositions.get(normalizedKey);
        return position ? `{{${position}}}` : match;
      }
    );

    // Check if text starts or ends with a variable ({{n}})
    const hasVariableAtEdge = (text: string) => {
      const trimmed = text.trim();
      return /^\{\{\d+\}\}/.test(trimmed) || /\{\{\d+\}\}$/.test(trimmed);
    };

    // Extract ordered unique variable IDs from normalized text
    const extractVariableIds = (text: string): string[] => {
      const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)];
      return [...new Set(matches.map(m => m[1]))];
    };

    const getVariableKeyByPosition = (id: string) => textVariableOrder[Number(id) - 1] || '';

    const buildBodyExample = (normalizedText: string) => {
      const ids = extractVariableIds(normalizedText);
      if (ids.length === 0) return undefined;
      return { body_text: [ids.map(id => VARIABLE_EXAMPLES_BY_KEY[getVariableKeyByPosition(id)] || `valor${id}`)] };
    };

    const buildHeaderExample = (normalizedText: string) => {
      const ids = extractVariableIds(normalizedText);
      if (ids.length === 0) return undefined;
      return { header_text: ids.map(id => VARIABLE_EXAMPLES_BY_KEY[getVariableKeyByPosition(id)] || `valor${id}`) };
    };

    // Validate: variables cannot be at start or end of body/header
    const normalizedBodyPreCheck = normalizeTemplateVariables(newBodyText.trim());
    if (hasVariableAtEdge(normalizedBodyPreCheck)) {
      return toast.error('Variáveis não podem estar no início ou no final do corpo do template. Adicione texto antes e depois da variável.');
    }
    if (headerHasText) {
      const normalizedHeaderPreCheck = normalizeTemplateVariables(newHeaderText.trim());
      if (hasVariableAtEdge(normalizedHeaderPreCheck)) {
        return toast.error('Variáveis não podem estar no início ou no final do cabeçalho. Adicione texto antes e depois da variável.');
      }
    }
    if (newHeaderFormat !== 'NONE' && newHeaderFormat !== 'TEXT' && !newHeaderMediaUrl.trim()) {
      return toast.error('Informe a URL https do arquivo de exemplo do cabeçalho. É esse arquivo que a Meta analisa na aprovação.');
    }
    // A Meta não aceita variável em rodapé. Deixar passar só adia a rejeição.
    if (extractFriendlyVariableOrder(newFooterText).length > 0 || /\{\{\s*[^}]+\s*\}\}/.test(newFooterText)) {
      return toast.error('O rodapé não aceita variáveis (regra da Meta). Mova a variável para o corpo.');
    }

    // Botões de link: a variável é numerada POR BOTÃO (sempre {{1}}) e precisa
    // ser o sufixo da URL, para a Meta conseguir revisar o domínio de destino.
    // Cada botão com variável vira UMA posição no final do variableOrder.
    const buttonVariableOrder: string[] = [];
    const normalizedButtons: any[] = [];
    for (const button of newButtons) {
      if (button.type === 'PHONE_NUMBER') {
        normalizedButtons.push({ type: 'phone_number', text: button.text, phone_number: button.phoneNumber });
        continue;
      }
      if (button.type !== 'URL') {
        normalizedButtons.push({ type: 'quick_reply', text: button.text });
        continue;
      }

      const rawUrl = String(button.url || '').trim();
      if (!rawUrl) return toast.error(`Informe a URL do botão "${button.text || ''}".`);

      const usedKeys = extractFriendlyVariableOrder(rawUrl);
      const rawPlaceholders = [...rawUrl.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)];

      if (rawPlaceholders.length === 0) {
        normalizedButtons.push({ type: 'url', text: button.text, url: rawUrl });
        continue;
      }
      if (rawPlaceholders.length > 1) {
        return toast.error(`O botão "${button.text || ''}" tem mais de uma variável no link. A Meta aceita apenas uma, no final da URL.`);
      }
      if (usedKeys.length === 0) {
        return toast.error(`A variável do link do botão "${button.text || ''}" não é reconhecida. Use uma das variáveis do sistema (ex: {{numeropedido}}).`);
      }
      if (!/\{\{\s*[^}]+?\s*\}\}\s*$/.test(rawUrl)) {
        return toast.error(`No botão "${button.text || ''}" a variável precisa ficar no FINAL do link (ex: https://site.com/pedido/{{numeropedido}}).`);
      }
      // A Meta revisa o destino do botão, então o domínio tem que ser fixo — a
      // variável é só o sufixo. URL que começa na variável é rejeitada.
      if (!/^https?:\/\/[^{\s]+/i.test(rawUrl)) {
        return toast.error(`O botão "${button.text || ''}" precisa de um domínio fixo antes da variável (ex: https://site.com/pedido/{{numeropedido}}). Para um link totalmente livre, coloque {{link}} no corpo da mensagem.`);
      }

      const variableKey = usedKeys[0];
      // {{1}} local ao botão, independente da numeração do corpo.
      const normalizedUrl = rawUrl.replace(/\{\{\s*[^}]+?\s*\}\}\s*$/, '{{1}}');
      const exampleValue = button.urlExample?.trim()
        || VARIABLE_EXAMPLES_BY_KEY[variableKey]
        || 'exemplo';
      const exampleUrl = /^https?:\/\//i.test(exampleValue)
        ? exampleValue
        : normalizedUrl.replace('{{1}}', exampleValue);

      normalizedButtons.push({ type: 'url', text: button.text, url: normalizedUrl, example: [exampleUrl] });
      buttonVariableOrder.push(variableKey);
    }

    if (newHeaderFormat === 'TEXT' && newHeaderText.trim()) {
      const normalizedHeader = normalizeTemplateVariables(newHeaderText.trim());
      const headerExample = buildHeaderExample(normalizedHeader);
      const headerComponent: any = { type: 'header', format: 'text', text: normalizedHeader };
      if (headerExample) headerComponent.example = headerExample;
      components.push(headerComponent);
    } else if (newHeaderFormat === 'IMAGE' || newHeaderFormat === 'VIDEO' || newHeaderFormat === 'DOCUMENT') {
      // Header de mídia não tem texto nem variável: leva só o asset de exemplo
      // que a Meta usa na revisão. No envio, a Zernio reaproveita esse asset
      // (ou usa headerMedia, quando informado).
      components.push({
        type: 'header',
        format: newHeaderFormat.toLowerCase(),
        example: { header_handle: [newHeaderMediaUrl.trim()] },
      });
    }

    const normalizedBody = normalizeTemplateVariables(newBodyText.trim());
    const bodyExample = buildBodyExample(normalizedBody);
    const bodyComponent: any = { type: 'body', text: normalizedBody };
    if (bodyExample) bodyComponent.example = bodyExample;
    components.push(bodyComponent);

    if (newFooterText.trim()) {
      components.push({ type: 'footer', text: newFooterText.trim() });
    }
    if (normalizedButtons.length > 0) {
      components.push({ type: 'buttons', buttons: normalizedButtons });
    }

    const templateVariableOrder = [...textVariableOrder, ...buttonVariableOrder];

    try {
      setCreatingTemplate(true);
      await api.createWhatsAppTemplate({
        accountId: selectedAccountId,
        name: sanitizeTemplateName(newName.trim()),
        category: newCategory,
        language: newLanguage,
        components,
        variableOrder: templateVariableOrder,
      });
      toast.success('Template enviado para aprovação do Meta!');
      setNewName('');
      setNewHeaderFormat('NONE');
      setNewHeaderText('');
      setNewHeaderMediaUrl('');
      setNewBodyText('');
      setNewFooterText('');
      setNewButtons([]);
      setActiveTab('templates');
      loadTemplates();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao criar template');
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (template: WhatsAppTemplate) => {
    if (!confirm(`Deseja realmente excluir o template "${template.name}"?`)) return;
    try {
      await api.deleteWhatsAppTemplate(template.name, selectedAccountId);
      toast.success('Template excluído');
      loadTemplates();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao excluir template');
    }
  };

  const handleCreateBroadcast = async () => {
    if (!broadcastName.trim()) return toast.error('Nome do broadcast é obrigatório');
    if (!broadcastTemplate) return toast.error('Selecione um template');

    const template = templates.find(t => t.name === broadcastTemplate);
    if (!template) return toast.error('Template não encontrado');

    // Parse recipients (one phone per line)
    const lines = broadcastRecipients.split('\n').filter(l => l.trim());
    const recipients = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      return {
        phone: parts[0],
        name: parts[1] || undefined,
        variables: parts[2] ? { '1': parts[2] } : undefined
      };
    });

    try {
      setCreatingBroadcast(true);
      await api.createWhatsAppBroadcast({
        accountId: selectedAccountId,
        name: broadcastName.trim(),
        template: { name: template.name, language: template.language },
        recipients,
        scheduledAt: broadcastSchedule || undefined
      });
      toast.success('Broadcast criado com sucesso!');
      setBroadcastName('');
      setBroadcastTemplate('');
      setBroadcastRecipients('');
      setBroadcastSchedule('');
      setShowBroadcastForm(false);
      loadBroadcasts();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao criar broadcast');
    } finally {
      setCreatingBroadcast(false);
    }
  };

  const handleSendBroadcast = async (broadcastId: string) => {
    if (!confirm('Deseja enviar este broadcast agora?')) return;
    try {
      await api.sendWhatsAppBroadcast(broadcastId);
      toast.success('Broadcast enviado!');
      loadBroadcasts();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao enviar broadcast');
    }
  };

  const handleDeleteBroadcast = async (broadcastId: string) => {
    if (!confirm('Deseja excluir este broadcast?')) return;
    try {
      await api.deleteWhatsAppBroadcast(broadcastId);
      toast.success('Broadcast excluído');
      loadBroadcasts();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao excluir broadcast');
    }
  };

  const addButton = () => {
    if (newButtons.length >= 3) return toast.error('Máximo de 3 botões');
    setNewButtons([...newButtons, { type: 'QUICK_REPLY', text: '' }]);
  };

  const removeButton = (index: number) => {
    setNewButtons(newButtons.filter((_, i) => i !== index));
  };

  const updateButton = (index: number, field: string, value: string) => {
    const updated = [...newButtons];
    (updated[index] as any)[field] = value;
    setNewButtons(updated);
  };

  const filteredTemplates = templates.filter(t => {
    const matchSearch = !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || t.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const approvedTemplates = templates.filter(t => t.status === 'APPROVED');

  // Count variables in text like {{1}}, {{2}}, etc.
  const countVariables = (text: string) => {
    const matches = text.match(/\{\{\s*([^}]+?)\s*\}\}/g);
    return matches ? matches.length : 0;
  };

  // Variáveis amigáveis disponíveis para templates
  // ERP: nome, primeironome, itens, numeropedido, numeronotafiscal, ecommerce, telefone
  // Contato: nome, primeironome, telefone, email, observacoes, categoria
  const FRIENDLY_VARIABLES = [
    { id: 1, label: 'Nome', key: 'nome' },
    { id: 2, label: 'Primeiro Nome', key: 'primeironome' },
    { id: 3, label: 'Telefone', key: 'telefone' },
    { id: 4, label: 'Email', key: 'email' },
    { id: 5, label: 'Itens', key: 'itens' },
    { id: 6, label: 'Nº Pedido', key: 'numeropedido' },
    { id: 7, label: 'Nº Nota Fiscal', key: 'numeronotafiscal' },
    { id: 8, label: 'E-commerce', key: 'ecommerce' },
    { id: 9, label: 'Observações', key: 'observacoes' },
    { id: 10, label: 'Categoria', key: 'categoria' },
    // Link vale tanto no corpo quanto no final da URL de um botão. Não é campo
    // do contato: no mapeamento vira texto fixo (uma URL), ou chega pelo payload
    // do webhook nas automações.
    { id: 11, label: 'Link (URL)', key: 'link' },
  ] as const;

  // Posições de um template APROVADO, na ordem em que a Zernio pede os valores:
  // cabeçalho de texto → corpo → um por botão de link dinâmico.
  //
  // Espelha extractTemplatePositions de backend/services/templateVariableMapping.
  // A versão antiga só varria `component.text` e deduplicava por número, então
  // (a) ignorava o link do botão e (b) fundia o {{1}} do botão com o {{1}} do
  // corpo — o mapeamento saía curto e o envio desalinhava.
  const extractTemplatePositions = (components?: any[]): Array<{ key: string; section: string; slot: string; label: string }> => {
    const list = Array.isArray(components) ? components : [];
    const typeOf = (component: any) => String(component?.type || '').toLowerCase();

    const slotsIn = (text: any): string[] => {
      const seen = new Set<string>();
      for (const match of String(text || '').matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
        seen.add(match[1]);
      }
      return [...seen].sort((a, b) => Number(a) - Number(b));
    };

    const positions: Array<{ key: string; section: string; slot: string; label: string }> = [];

    const header = list.find(component => typeOf(component) === 'header');
    if (header && String(header.format || 'text').toLowerCase() === 'text') {
      slotsIn(header.text).forEach(slot => positions.push({
        key: `header:${slot}`, section: 'header', slot, label: `Cabeçalho {{${slot}}}`
      }));
    }

    const body = list.find(component => typeOf(component) === 'body');
    if (body) {
      slotsIn(body.text).forEach(slot => positions.push({
        key: `body:${slot}`, section: 'body', slot, label: `Corpo {{${slot}}}`
      }));
    }

    let buttonIndex = 0;
    list.filter(component => typeOf(component) === 'buttons').forEach(component => {
      const buttons = Array.isArray(component.buttons) ? component.buttons : [];
      buttons.forEach((button: any) => {
        const currentIndex = buttonIndex++;
        if (String(button?.type || '').toLowerCase() !== 'url') return;
        if (!/\{\{\s*\d+\s*\}\}/.test(String(button?.url || ''))) return;
        positions.push({
          key: `button:${currentIndex}`,
          section: 'button',
          slot: String(currentIndex),
          label: `Botão "${button?.text || currentIndex + 1}" — final do link`
        });
      });
    });

    return positions;
  };

  const insertFriendlyVariable = (key: string) => {
    setNewBodyText(prev => `${prev}{{${key}}}`);
  };

  const extractFriendlyVariableOrder = (text: string): string[] => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    const matches = text.matchAll(/\{\{\s*([^}]+?)\s*\}\}/gi);

    for (const match of matches) {
      const key = String(match[1] || '').trim().toLowerCase();
      const exists = FRIENDLY_VARIABLES.some(variable => variable.key === key);
      if (exists && !seen.has(key)) {
        seen.add(key);
        ordered.push(key);
      }
    }

    return ordered;
  };

  // Prévia do mapeamento no formulário de criação. Reproduz exatamente a ordem
  // que handleCreateTemplate monta: cabeçalho de texto → corpo → botões de link.
  // O botão NÃO entra no Set de dedupe: mesmo repetindo uma variável do corpo,
  // ele ocupa uma posição própria (a Zernio pede um valor por botão dinâmico).
  const usedFriendlyVariableOrder = (() => {
    const textKeys = [
      ...new Set([
        ...(newHeaderFormat === 'TEXT' ? extractFriendlyVariableOrder(newHeaderText) : []),
        ...extractFriendlyVariableOrder(newBodyText),
      ])
    ];
    const entries = textKeys
      .map(key => FRIENDLY_VARIABLES.find(variable => variable.key === key))
      .filter((variable): variable is (typeof FRIENDLY_VARIABLES)[number] => Boolean(variable))
      .map(variable => ({ key: variable.key, label: variable.label, origin: 'texto' }));

    newButtons.forEach(button => {
      if (button.type !== 'URL') return;
      const keys = extractFriendlyVariableOrder(button.url || '');
      if (keys.length === 0) return;
      const variable = FRIENDLY_VARIABLES.find(v => v.key === keys[0]);
      if (!variable) return;
      entries.push({ key: variable.key, label: variable.label, origin: `link do botão "${button.text || ''}"` });
    });

    return entries;
  })();

  useEffect(() => {
    if (!selectedTemplate) {
      setSelectedTemplateVariableOrder([]);
      return;
    }

    const positions = extractTemplatePositions(selectedTemplate.components);
    setSelectedTemplateVariableOrder(
      positions.map((_, index) => selectedTemplate.variableOrder?.[index] || '')
    );
  }, [selectedTemplate]);

  const openTemplatePreview = (template: WhatsAppTemplate) => {
    setTemplateDetailMode('preview');
    setSelectedTemplate(template);
  };

  const openTemplateMapping = (template: WhatsAppTemplate) => {
    setTemplateDetailMode('mapping');
    setSelectedTemplate(template);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Carregando contas WhatsApp...</p>
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.885-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-3">Nenhuma conta WhatsApp Business conectada</h2>
          <p className="text-gray-500 mb-6">
            Para gerenciar templates e broadcasts, primeiro conecte uma conta WhatsApp Business 
            na página de <strong>APIs Oficiais</strong> (dentro de Conexões).
          </p>
          <a
            href="/redes-sociais"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Conectar WhatsApp Business
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.885-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            Templates WhatsApp
          </h1>
          <p className="text-sm text-gray-500 mt-1">Gerencie templates de mensagem e broadcasts do WhatsApp Business API</p>
        </div>

        {/* Account selector */}
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 font-medium">Conta:</label>
          <select
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            {accounts.map(a => (
              <option key={a.id} value={a.platformAccountId || a.id}>
                {a.accountName} {a.accountUsername ? `(${a.accountUsername})` : ''} - {a.groupName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { id: 'templates' as TabType, label: 'Templates', count: templates.length },
          { id: 'criar' as TabType, label: 'Criar Template', count: null },
          { id: 'broadcasts' as TabType, label: 'Broadcasts', count: broadcasts.length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-green-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count !== null && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === tab.id ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ============ TAB: Templates ============ */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          {/* Search and filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Buscar templates..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white"
            >
              <option value="">Todos os status</option>
              <option value="APPROVED">Aprovados</option>
              <option value="PENDING">Em revisão</option>
              <option value="REJECTED">Rejeitados</option>
            </select>
            <button
              onClick={loadTemplates}
              disabled={loadingTemplates}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
            >
              <svg className={`w-4 h-4 ${loadingTemplates ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Atualizar
            </button>
          </div>

          {loadingTemplates ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-3"></div>
              <p className="text-gray-500 text-sm">Carregando templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-500">Nenhum template encontrado</p>
              <button
                onClick={() => setActiveTab('criar')}
                className="mt-3 text-green-600 hover:text-green-700 text-sm font-medium"
              >
                Criar primeiro template
              </button>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredTemplates.map((template, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-800 truncate">{template.name}</h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[template.status] || 'bg-gray-100 text-gray-500'}`}>
                          {STATUS_LABELS[template.status] || template.status}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600">
                          {template.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span>Idioma: {template.language}</span>
                        {template.components && (
                          <span>{template.components.length} componente(s)</span>
                        )}
                        {template.rejectedReason && (
                          <span className="text-red-500">Motivo: {template.rejectedReason}</span>
                        )}
                      </div>
                      {/* Show body preview */}
                      {template.components && template.components.map((comp: any, ci: number) => (
                        comp.type === 'BODY' && (
                          <p key={ci} className="mt-2 text-sm text-gray-600 line-clamp-2">{comp.text}</p>
                        )
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      {extractTemplatePositions(template.components).length > 0 && (
                        <button
                          onClick={() => openTemplateMapping(template)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                          title="Editar mapeamento"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Editar mapeamento
                        </button>
                      )}
                      <button
                        onClick={() => openTemplatePreview(template)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Pré-visualização"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Pré-visualização
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ TAB: Criar Template ============ */}
      {activeTab === 'criar' && (
        <div className="max-w-3xl space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-800">Criar novo template</h2>
            <p className="text-sm text-gray-500">
              Templates são revisados pelo Meta e podem levar até 24h para aprovação.
              Use os botões de variáveis abaixo para inserir dados dinâmicos.
            </p>
            <p className="text-xs text-amber-600 -mt-2">
              Para criação manual de template customizado, use uma categoria compatível com a Meta: <strong>Autenticação</strong>, <strong>Marketing</strong> ou <strong>Utilidade</strong>.
            </p>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome do template</label>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(sanitizeTemplateName(e.target.value))}
                placeholder="ex: confirmacao_pedido"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">Apenas letras minúsculas, números e underscore, começando com letra</p>
            </div>

            {/* Category + Language */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={newCategory}
                  onChange={e => setNewCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label} - {cat.description}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Idioma</label>
                <select
                  value={newLanguage}
                  onChange={e => setNewLanguage(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Header */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cabeçalho <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {([
                  { value: 'NONE', label: 'Sem cabeçalho' },
                  { value: 'TEXT', label: 'Texto' },
                  { value: 'IMAGE', label: 'Imagem' },
                  { value: 'VIDEO', label: 'Vídeo' },
                  { value: 'DOCUMENT', label: 'Documento' },
                ] as const).map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setNewHeaderFormat(option.value)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                      newHeaderFormat === option.value
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {newHeaderFormat === 'TEXT' && (
                <>
                  <input
                    type="text"
                    value={newHeaderText}
                    onChange={e => setNewHeaderText(e.target.value)}
                    placeholder="Texto do cabeçalho..."
                    maxLength={60}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">{newHeaderText.length}/60</p>
                </>
              )}

              {(newHeaderFormat === 'IMAGE' || newHeaderFormat === 'VIDEO' || newHeaderFormat === 'DOCUMENT') && (
                <>
                  <div className="flex items-center gap-2">
                    <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                      uploadingHeaderMedia
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-wait'
                        : 'bg-white text-green-700 border-green-300 hover:bg-green-50'
                    }`}>
                      {uploadingHeaderMedia ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          {newHeaderMediaUrl ? 'Trocar arquivo' : 'Escolher arquivo'}
                        </>
                      )}
                      <input
                        type="file"
                        className="hidden"
                        disabled={uploadingHeaderMedia}
                        accept={HEADER_ACCEPT[newHeaderFormat]}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) handleUploadHeaderMedia(file);
                        }}
                      />
                    </label>
                    {newHeaderMediaUrl && !uploadingHeaderMedia && (
                      <button
                        type="button"
                        onClick={() => setNewHeaderMediaUrl('')}
                        className="text-xs text-red-500 hover:text-red-600"
                      >
                        remover
                      </button>
                    )}
                  </div>

                  {newHeaderMediaUrl && (
                    <p className="text-xs text-green-600 mt-1 break-all">Arquivo no ar: {newHeaderMediaUrl}</p>
                  )}

                  <p className="text-xs text-gray-400 mt-1">
                    O arquivo é hospedado no nosso storage e a Meta baixa dele para revisar o template. Depois de
                    aprovado, esse mesmo arquivo vai em todas as mensagens.{' '}
                    {newHeaderFormat === 'IMAGE' && 'Imagem: JPG ou PNG, até 5 MB.'}
                    {newHeaderFormat === 'VIDEO' && 'Vídeo: MP4, até 16 MB.'}
                    {newHeaderFormat === 'DOCUMENT' && 'Documento: PDF, até 100 MB.'}
                  </p>
                </>
              )}
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Corpo da mensagem *</label>
              <textarea
                value={newBodyText}
                onChange={e => setNewBodyText(e.target.value)}
                placeholder="Olá {{nome}}, seu pedido {{numeropedido}} foi confirmado!"
                rows={4}
                maxLength={1024}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{countVariables(newBodyText)} variável(is) detectada(s)</span>
                <span>{newBodyText.length}/1024</span>
              </div>

              {/* Botões de variáveis amigáveis */}
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-500 mb-1.5">Inserir variável:</p>
                <div className="flex flex-wrap gap-1.5">
                  {FRIENDLY_VARIABLES.map(v => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertFriendlyVariable(v.key)}
                      className="px-2.5 py-1 bg-green-50 text-green-700 text-xs rounded-md hover:bg-green-100 border border-green-200 transition-colors"
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tabela de mapeamento */}
              {usedFriendlyVariableOrder.length > 0 && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-blue-700 mb-2">Mapeamento de variáveis:</p>
                  <div className="flex flex-wrap gap-2">
                    {usedFriendlyVariableOrder.map((variable, index) => (
                      <div key={`${variable.origin}:${variable.key}:${index}`} className="flex items-center gap-1.5 bg-white rounded px-2 py-1 border border-blue-100">
                        <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">{`{{${variable.key}}}`}</code>
                        <span className="text-xs text-blue-400">→</span>
                        <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">{`{{${index + 1}}}`}</code>
                        <span className="text-xs text-gray-500">=</span>
                        <span className="text-xs font-medium text-gray-700">{variable.label}</span>
                        <span className="text-[10px] text-gray-400">({variable.origin})</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-500 mt-2">
                    Ao criar o template, você escreve com nome amigável (ex: <code className="bg-blue-100 px-1 rounded">{`{{nome}}`}</code>) e o sistema envia para a API com numeração sequencial conforme a ordem em que as variáveis aparecem no template.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rodapé <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <input
                type="text"
                value={newFooterText}
                onChange={e => setNewFooterText(e.target.value)}
                placeholder="Texto do rodapé..."
                maxLength={60}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Buttons */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Botões <span className="text-gray-400 font-normal">(opcional, máx 3)</span>
                </label>
                <button
                  onClick={addButton}
                  disabled={newButtons.length >= 3}
                  className="text-sm text-green-600 hover:text-green-700 font-medium disabled:opacity-50"
                >
                  + Adicionar botão
                </button>
              </div>
              {newButtons.map((btn, idx) => {
                const btnHasVariable = btn.type === 'URL' && /\{\{\s*[^}]+?\s*\}\}/.test(btn.url || '');
                return (
                <div key={idx} className="mb-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <select
                      value={btn.type}
                      onChange={e => updateButton(idx, 'type', e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-2 text-sm w-36"
                    >
                      <option value="QUICK_REPLY">Resposta rápida</option>
                      <option value="URL">Link URL</option>
                      <option value="PHONE_NUMBER">Telefone</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Texto do botão"
                      value={btn.text}
                      onChange={e => updateButton(idx, 'text', e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm"
                      maxLength={25}
                    />
                    {btn.type === 'URL' && (
                      <input
                        type="text"
                        placeholder="https://site.com/pedido/{{numeropedido}}"
                        value={btn.url || ''}
                        onChange={e => updateButton(idx, 'url', e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm"
                      />
                    )}
                    {btn.type === 'PHONE_NUMBER' && (
                      <input
                        type="text"
                        placeholder="+5511999999999"
                        value={btn.phoneNumber || ''}
                        onChange={e => updateButton(idx, 'phoneNumber', e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm"
                      />
                    )}
                    <button
                      onClick={() => removeButton(idx)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {btn.type === 'URL' && (
                    <div className="mt-2 pl-1">
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        <span className="text-xs text-gray-500">Inserir variável no fim do link:</span>
                        {FRIENDLY_VARIABLES.map(v => (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => updateButton(idx, 'url', `${(btn.url || '').replace(/\{\{\s*[^}]+?\s*\}\}\s*$/, '')}{{${v.key}}}`)}
                            className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded border border-green-200 hover:bg-green-100"
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>

                      {btnHasVariable ? (
                        <>
                          <input
                            type="text"
                            placeholder="URL completa de exemplo (ex: https://site.com/pedido/12345)"
                            value={btn.urlExample || ''}
                            onChange={e => updateButton(idx, 'urlExample', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            A Meta exige um exemplo real para aprovar link dinâmico. Se ficar em branco, geramos um a
                            partir da variável. A variável precisa ser a última parte da URL, e só uma por botão —
                            ela vira uma posição no mapeamento, preenchida por campanha e automação.
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-gray-400">
                          Dica: para um link dinâmico, termine a URL com uma variável — ex:{' '}
                          <code className="bg-gray-100 px-1 rounded">{`https://site.com/pedido/{{numeropedido}}`}</code>.
                          O domínio precisa ser fixo (a Meta revisa o destino); para um link totalmente livre,
                          use <code className="bg-gray-100 px-1 rounded">{`{{link}}`}</code> no corpo da mensagem.
                        </p>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* Preview */}
            {(newHeaderText || newHeaderMediaUrl || newBodyText || newFooterText) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pré-visualização</label>
                <div className="bg-[#e5ddd5] rounded-xl p-4">
                  <div className="max-w-xs bg-white rounded-lg shadow-sm p-3 space-y-1">
                    {newHeaderFormat === 'IMAGE' && newHeaderMediaUrl && (
                      <img src={newHeaderMediaUrl} alt="" className="w-full rounded-md mb-1 object-cover max-h-40" />
                    )}
                    {newHeaderFormat === 'VIDEO' && newHeaderMediaUrl && (
                      <video src={newHeaderMediaUrl} controls className="w-full rounded-md mb-1 max-h-40" />
                    )}
                    {newHeaderFormat === 'DOCUMENT' && newHeaderMediaUrl && (
                      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-2 py-2 mb-1">
                        <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-gray-500 truncate">{newHeaderMediaUrl.split('/').pop()}</span>
                      </div>
                    )}
                    {newHeaderFormat === 'TEXT' && newHeaderText && (
                      <p className="font-bold text-sm text-gray-900">{newHeaderText}</p>
                    )}
                    {newBodyText && (
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{newBodyText}</p>
                    )}
                    {newFooterText && (
                      <p className="text-xs text-gray-400 mt-1">{newFooterText}</p>
                    )}
                    {newButtons.length > 0 && (
                      <div className="border-t border-gray-100 pt-2 mt-2 space-y-1">
                        {newButtons.map((btn, i) => (
                          <div key={i} className="text-center text-sm text-blue-500 font-medium py-1 border border-blue-100 rounded">
                            {btn.text || 'Botão'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleCreateTemplate}
              disabled={creatingTemplate || !newName || !newBodyText}
              className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {creatingTemplate ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enviando para aprovação...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Enviar para Aprovação do Meta
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ============ TAB: Broadcasts ============ */}
      {activeTab === 'broadcasts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Envie templates aprovados para múltiplos destinatários de uma vez.
            </p>
            <button
              onClick={() => setShowBroadcastForm(!showBroadcastForm)}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Broadcast
            </button>
          </div>

          {/* Broadcast Form */}
          {showBroadcastForm && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <h3 className="font-semibold text-gray-800">Novo Broadcast</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={broadcastName}
                    onChange={e => setBroadcastName(e.target.value)}
                    placeholder="Ex: Newsletter Março"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template (aprovado)</label>
                  <select
                    value={broadcastTemplate}
                    onChange={e => setBroadcastTemplate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Selecione...</option>
                    {approvedTemplates.map(t => (
                      <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
                    ))}
                  </select>
                  {approvedTemplates.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">Nenhum template aprovado disponível</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destinatários (um por linha: telefone, nome, variável1)
                </label>
                <textarea
                  value={broadcastRecipients}
                  onChange={e => setBroadcastRecipients(e.target.value)}
                  placeholder={"+5511999999999, João, João\n+5511888888888, Maria, Maria"}
                  rows={6}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {broadcastRecipients.split('\n').filter(l => l.trim()).length} destinatário(s)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agendar envio <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <input
                  type="datetime-local"
                  value={broadcastSchedule}
                  onChange={e => setBroadcastSchedule(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCreateBroadcast}
                  disabled={creatingBroadcast || !broadcastName || !broadcastTemplate}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 text-sm"
                >
                  {creatingBroadcast ? 'Criando...' : 'Criar Broadcast'}
                </button>
                <button
                  onClick={() => setShowBroadcastForm(false)}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Broadcast List */}
          {loadingBroadcasts ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-3"></div>
              <p className="text-gray-500 text-sm">Carregando broadcasts...</p>
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
              <p className="text-gray-500">Nenhum broadcast criado ainda</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {broadcasts.map((bc) => (
                <div key={bc.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-800">{bc.name}</h3>
                      <div className="flex items-center gap-4 text-xs text-gray-400 mt-1">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${
                          bc.status === 'sent' ? 'bg-green-100 text-green-700' :
                          bc.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          bc.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {bc.status === 'sent' ? 'Enviado' : bc.status === 'scheduled' ? 'Agendado' : bc.status === 'draft' ? 'Rascunho' : bc.status}
                        </span>
                        {bc.recipientCount && <span>{bc.recipientCount} destinatários</span>}
                        {bc.sent !== undefined && <span>Enviados: {bc.sent}</span>}
                        {bc.failed !== undefined && bc.failed > 0 && <span className="text-red-500">Falhas: {bc.failed}</span>}
                        {bc.scheduledAt && <span>Agendado: {new Date(bc.scheduledAt).toLocaleString('pt-BR')}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(bc.status === 'draft' || bc.status === 'pending') && (
                        <button
                          onClick={() => handleSendBroadcast(bc.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Enviar
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteBroadcast(bc.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ Template Detail Modal ============ */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTemplate(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">{templateDetailMode === 'mapping' ? 'Editar mapeamento do template' : 'Pré-visualização do template'}</h2>
                <button onClick={() => setSelectedTemplate(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 w-20">Nome:</span>
                  <span className="text-sm font-mono bg-gray-50 px-2 py-1 rounded">{selectedTemplate.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 w-20">Status:</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[selectedTemplate.status] || 'bg-gray-100'}`}>
                    {STATUS_LABELS[selectedTemplate.status] || selectedTemplate.status}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 w-20">Categoria:</span>
                  <span className="text-sm">{selectedTemplate.category}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 w-20">Idioma:</span>
                  <span className="text-sm">{selectedTemplate.language}</span>
                </div>
                {selectedTemplate.rejectedReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700"><strong>Motivo da rejeição:</strong> {selectedTemplate.rejectedReason}</p>
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Pré-visualização</label>
                <div className="bg-[#e5ddd5] rounded-xl p-4">
                  <div className="max-w-xs bg-white rounded-lg shadow-sm p-3 space-y-1">
                    {selectedTemplate.components?.map((comp: any, ci: number) => (
                      <div key={ci}>
                        {(comp.type === 'HEADER' || comp.type === 'header') && (() => {
                          const format = String(comp.format || 'text').toLowerCase();
                          const handle = Array.isArray(comp?.example?.header_handle)
                            ? comp.example.header_handle[0]
                            : comp?.example?.header_handle;
                          if (format === 'image' && handle) {
                            return <img src={handle} alt="" className="w-full rounded-md mb-1 object-cover max-h-40" />;
                          }
                          if (format === 'video' && handle) {
                            return <video src={handle} controls className="w-full rounded-md mb-1 max-h-40" />;
                          }
                          if (format !== 'text') {
                            return <p className="text-xs text-gray-400 italic mb-1">Cabeçalho de {format}</p>;
                          }
                          return <p className="font-bold text-sm text-gray-900">{comp.text}</p>;
                        })()}
                        {(comp.type === 'BODY' || comp.type === 'body') && <p className="text-sm text-gray-800 whitespace-pre-wrap">{comp.text}</p>}
                        {(comp.type === 'FOOTER' || comp.type === 'footer') && <p className="text-xs text-gray-400 mt-1">{comp.text}</p>}
                        {(comp.type === 'BUTTONS' || comp.type === 'buttons') && (
                          <div className="border-t border-gray-100 pt-2 mt-2 space-y-1">
                            {comp.buttons?.map((btn: any, bi: number) => (
                              <div key={bi} className="text-center text-sm text-blue-500 font-medium py-1 border border-blue-100 rounded">
                                {btn.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {templateDetailMode === 'mapping' && extractTemplatePositions(selectedTemplate.components).length > 0 && (
                <div className="mt-6 border border-blue-200 bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-blue-800">Mapeamento central do template</h3>
                      <p className="text-xs text-blue-600 mt-1">Esse mapeamento será reutilizado por campanhas e automações ERP.</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {extractTemplatePositions(selectedTemplate.components).map((position, index) => {
                      const entry = selectedTemplateVariableOrder[index] || '';
                      const isLiteral = entry.startsWith(TEXT_MAPPING_PREFIX);
                      const setEntry = (value: string) => setSelectedTemplateVariableOrder(prev => {
                        const next = [...prev];
                        next[index] = value;
                        return next;
                      });
                      return (
                        <div key={position.key} className="grid grid-cols-[140px_1fr] gap-3 items-start">
                          <div className="bg-white border border-blue-200 rounded px-2 py-2 text-center">
                            <span className="block text-xs font-mono text-blue-700">{`{{${index + 1}}}`}</span>
                            <span className="block text-[10px] text-blue-500 mt-0.5 leading-tight">{position.label}</span>
                          </div>
                          <div className="space-y-2">
                            {/* Variável do sistema × texto fixo. O texto fixo é gravado
                                com o prefixo "text:" e vale igual para todos os contatos. */}
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                onClick={() => setEntry(isLiteral ? '' : entry)}
                                className={`px-2.5 py-1 rounded-md text-xs font-medium border ${!isLiteral ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-100'}`}
                              >
                                Variável do sistema
                              </button>
                              <button
                                type="button"
                                onClick={() => setEntry(isLiteral ? entry : TEXT_MAPPING_PREFIX)}
                                className={`px-2.5 py-1 rounded-md text-xs font-medium border ${isLiteral ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-100'}`}
                              >
                                Texto fixo
                              </button>
                            </div>

                            {isLiteral ? (
                              <input
                                type="text"
                                value={entry.slice(TEXT_MAPPING_PREFIX.length)}
                                onChange={(e) => setEntry(`${TEXT_MAPPING_PREFIX}${e.target.value}`)}
                                placeholder="Texto que aparecerá nesta posição (ex.: Black Friday)"
                                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <select
                                value={entry}
                                onChange={(e) => setEntry(e.target.value)}
                                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Selecione a variável do sistema...</option>
                                {FRIENDLY_VARIABLES.map(variable => (
                                  <option key={variable.key} value={variable.key}>{variable.label}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={handleSaveTemplateMapping}
                    disabled={savingTemplateMapping}
                    className="w-full mt-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-60"
                  >
                    {savingTemplateMapping ? 'Salvando...' : 'Salvar mapeamento do template'}
                  </button>
                </div>
              )}

              <button
                onClick={() => setSelectedTemplate(null)}
                className="w-full mt-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
